import { useState, useRef, useEffect } from 'react';
import Recorder from 'recorder-core';
import 'recorder-core/src/engine/wav'; // используем WAV для Whisper

import { enqueueRecording, dequeueAll } from '../utils/offlineQueue';

const ENDPOINT = '/v0/whisper';
const MAX_DURATION_MS = 10 * 60 * 1000;

export function useVoiceInput({
  tinode,
  onTextReady,
  onUploading,
  onRecording,
  onError,
  locale
}) {
  const [isRecording, setRecording] = useState(false);
  const [isUploading, setUploading] = useState(false);
  const recRef = useRef(null);
  const timerRef = useRef(null);

  const startRecording = () => {
    if (isRecording) return;

    const rec = Recorder({
      type: 'wav',
      sampleRate: 16000,
      bitRate: 16
    });

    rec.open(() => {
      rec.start();
      recRef.current = rec;
      setRecording(true);
      onRecording?.(true);

      timerRef.current = setTimeout(() => {
        onError?.('voice.error.limit');
        stopRecording();
      }, MAX_DURATION_MS);
    }, (errMsg) => {
      console.warn('Recorder open failed:', errMsg);
      onError?.('voice.error.mediaUnsupported');
    });
  };

  const stopRecording = () => {
    if (!recRef.current || !isRecording) return;

    clearTimeout(timerRef.current);

    recRef.current.stop(async (blob) => {
      setRecording(false);
      onRecording?.(false);
      recRef.current.close();
      recRef.current = null;

      if (!navigator.onLine) {
        await enqueueRecording(blob);
        return;
      }

      handleBlob(blob);
    }, (errMsg) => {
      console.error('Stop recording failed:', errMsg);
      onError?.('voice.error.recording');
    });
  };

  const handleBlob = async (blob) => {
    setUploading(true);
    onUploading?.(true);

    try {
      await transcribe(blob, locale);
    } catch (err) {
      console.error('Upload failed, enqueueing:', err);
      await enqueueRecording(blob);
    } finally {
      setUploading(false);
      onUploading?.(false);
    }
  };

  const transcribe = async (blob, locale) => {
    const fd = new FormData();
    fd.append('file', blob, 'speech.wav');
    if (locale) fd.append('language', locale);

    const { token } = tinode.getAuthToken();
    const r = await fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'X-Tinode-APIKey': tinode._apiKey,
        'X-Tinode-Auth': `Token ${token}`,
      },
      body: fd,
    });

    if (!r.ok) throw new Error(await r.text());
    const { text } = await r.json();
    onTextReady(text.trim());
  };

  const processOfflineQueue = async () => {
    if (!navigator.onLine) return;

    const entries = await dequeueAll();
    for (const item of entries) {
      try {
        await transcribe(item.blob, locale);
      } catch (err) {
        console.warn('Failed to resend queued blob, re-enqueueing:', err);
        await enqueueRecording(item.blob);
        break; // остановим — сеть нестабильна
      }
    }
  };

  useEffect(() => {
    processOfflineQueue(); // при монтировании
    const onOnline = () => processOfflineQueue();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, []);

  return {
    isRecording,
    isUploading,
    startRecording,
    stopRecording
  };
}
